// Static "Plan Templates" — casual activity ideas shown as a carousel on the
// Community Plans screen to remove the blank-page problem of starting a
// plan from scratch. These are NOT plans: selecting one only pre-fills the
// existing Create Plan form (see UploadPlanScreen's `initialValues` route
// param) — nothing here ever touches Firestore on its own. Every value is
// freely editable by the user before they post.
export type PlanTemplate = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  location: string;
  vibe: string[];
  participantLimit: number;
};

export const PLAN_TEMPLATES: PlanTemplate[] = [
  {
    id: 'jam-alaunpark',
    emoji: '🎸',
    title: 'Jam Session in Alaunpark',
    description: "Anyone up for a little jam session in Alaunpark? 🎸 Bring your guitar, ukulele, cajón or whatever you play. No need to be a pro — you can also just come hang out and listen.",
    location: 'Alaunpark, Dresden',
    vibe: ['Music', 'Chill'],
    participantLimit: 10,
  },
  {
    id: 'flunkyball',
    emoji: '🍻',
    title: 'Flunkyball in Großer Garten',
    description: "Anyone up for a round of Flunkyball? 🍻 Beginners are welcome too — we'll figure out the teams when everyone gets there. Bring something to drink and let's play.",
    location: 'Großer Garten, Dresden',
    vibe: ['Games', 'Social'],
    participantLimit: 12,
  },
  {
    id: 'bar-hopping-neustadt',
    emoji: '🍺',
    title: 'Bar Hopping in Neustadt',
    description: "Let's grab some drinks and hop around a few bars in Neustadt 🍻 No strict route — we'll start somewhere together and figure out where to go next.",
    location: 'Äußere Neustadt, Dresden',
    vibe: ['Night out', 'Social'],
    participantLimit: 8,
  },
  {
    id: 'game-night-trotzdem',
    emoji: '🎲',
    title: 'Game Night at Trotzdem',
    description: "Anyone up for a relaxed game night at Trotzdem? 🎲 Come alone or bring someone along. We'll grab some drinks, pick some games and see where the evening goes.",
    location: 'Trotzdem, Dresden',
    vibe: ['Games', 'Chill'],
    participantLimit: 8,
  },
  {
    id: 'sunset-hangout-elbe',
    emoji: '🌅',
    title: 'Sunset Hangout at the Elbe',
    description: "Weather looking good? Let's meet by the Elbe, bring some drinks or snacks and watch the sunset together 🌅 Nothing complicated, just a chill evening outside.",
    location: 'Elbwiesen, Dresden',
    vibe: ['Chill', 'Outdoors'],
    participantLimit: 10,
  },
  {
    id: 'picnic-alaunpark',
    emoji: '🧺',
    title: 'Picnic in Alaunpark',
    description: "Let's have a casual picnic in Alaunpark 🧺 Bring a snack, something to drink or just yourself. We can sit around, talk, play some music and enjoy the weather.",
    location: 'Alaunpark, Dresden',
    vibe: ['Chill', 'Social'],
    participantLimit: 10,
  },
  {
    id: 'volleyball-alaunpark',
    emoji: '🏐',
    title: 'Volleyball in Alaunpark',
    description: "Anyone up for some casual volleyball? 🏐 Doesn't matter if you're good or haven't played in ages. Let's get enough people together and have a few games.",
    location: 'Alaunpark, Dresden',
    vibe: ['Active', 'Games'],
    participantLimit: 12,
  },
  {
    id: 'karaoke-night',
    emoji: '🎤',
    title: 'Karaoke Night',
    description: "Who's up for karaoke? 🎤 Good singers, terrible singers and people who only want to watch are all welcome. Let's get a small group together and make a night of it.",
    location: 'Dresden',
    vibe: ['Night out', 'Music'],
    participantLimit: 8,
  },
  {
    id: 'photo-walk-neustadt',
    emoji: '📸',
    title: 'Photo Walk through Neustadt',
    description: "Let's wander around Neustadt and take some photos 📸 Phone cameras absolutely count. No planned route — just walk around, find interesting spots and maybe grab something afterwards.",
    location: 'Äußere Neustadt, Dresden',
    vibe: ['Creative', 'Explore'],
    participantLimit: 6,
  },
  {
    id: 'elbe-bike-ride',
    emoji: '🚲',
    title: 'Bike Ride along the Elbe',
    description: "Anyone want to go for a relaxed bike ride along the Elbe? 🚲 Nothing competitive — we'll pick a route together, make a few stops and enjoy the ride.",
    location: 'Elberadweg, Dresden',
    vibe: ['Active', 'Outdoors'],
    participantLimit: 8,
  },
  {
    id: 'weekend-hike',
    emoji: '🥾',
    title: 'Weekend Hike in Saxon Switzerland',
    description: "Anyone up for escaping Dresden for a hike this weekend? 🥾 We can decide the exact route together depending on who's joining. No need to come with a group — that's the whole point.",
    location: 'Sächsische Schweiz',
    vibe: ['Outdoors', 'Adventure'],
    participantLimit: 8,
  },
  {
    id: 'coffee-meetup',
    emoji: '☕',
    title: 'Coffee & Meet New People',
    description: "Feel like meeting some new people over coffee? ☕ Let's pick a nice café in Neustadt and hang out for a bit. Come alone — everyone else probably will too.",
    location: 'Äußere Neustadt, Dresden',
    vibe: ['Meet people', 'Chill'],
    participantLimit: 6,
  },
  {
    id: 'international-meetup',
    emoji: '🌍',
    title: 'International Meetup',
    description: "New in Dresden or just want to meet people from different places? 🌍 Let's get together for some drinks, conversations and new connections. Locals are obviously welcome too.",
    location: 'Dresden',
    vibe: ['International', 'Social'],
    participantLimit: 10,
  },
  {
    id: 'german-conversation',
    emoji: '🇩🇪',
    title: 'German Conversation Hangout',
    description: "Want to practice some German without sitting in a classroom? 🇩🇪 Let's meet up, grab something to drink and just talk. All levels are welcome — mistakes are part of it.",
    location: 'Dresden',
    vibe: ['Language', 'Social'],
    participantLimit: 8,
  },
  {
    id: 'billiards-night',
    emoji: '🎱',
    title: 'Billiards Night',
    description: "Anyone up for a few rounds of billiards? 🎱 Skill level doesn't matter. Let's get a small group together, play some games and grab a drink.",
    location: 'Dresden',
    vibe: ['Games', 'Social'],
    participantLimit: 6,
  },
  {
    id: 'music-chill-elbe',
    emoji: '🎧',
    title: 'Music & Chill by the Elbe',
    description: "Let's grab a speaker, sit by the Elbe and listen to some music 🎧 Bring something to drink, suggest songs and just hang out.",
    location: 'Elbwiesen, Dresden',
    vibe: ['Music', 'Chill'],
    participantLimit: 10,
  },
];
